import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { IMetricDefinition, IMetricGroup } from '@skystone/shared';
import { metricDefinitionsApi } from '@/services/metric-definitions-api';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface MetricDefinitionsContextValue {
  groups: IMetricGroup[];
  definitions: IMetricDefinition[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const MetricDefinitionsContext = createContext<MetricDefinitionsContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function MetricDefinitionsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [groups, setGroups] = useState<IMetricGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDefinitions = useCallback(() => {
    setIsLoading(true);
    setError(null);
    metricDefinitionsApi
      .getAll()
      .then((data) => {
        // Guard against malformed responses: a missing `groups` field would
        // set state to undefined and crash `groups.flatMap(...)` below,
        // which unmounts the whole app since this provider is at the root.
        setGroups(data?.groups ?? []);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to load metric definitions';
        setError(message);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  const value = useMemo(
    () => ({
      groups,
      definitions: groups.flatMap((g) => g.metrics),
      isLoading,
      error,
      refetch: fetchDefinitions,
    }),
    [groups, isLoading, error, fetchDefinitions],
  );

  return (
    <MetricDefinitionsContext.Provider value={value}>
      {children}
    </MetricDefinitionsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const EMPTY_CONTEXT: MetricDefinitionsContextValue = {
  groups: [],
  definitions: [],
  isLoading: true,
  error: null,
  refetch: () => {},
};

export function useMetricDefinitions(): MetricDefinitionsContextValue {
  const ctx = useContext(MetricDefinitionsContext);
  return ctx ?? EMPTY_CONTEXT;
}

export function useMetricDefinition(metricKey: string): IMetricDefinition | undefined {
  const { definitions } = useMetricDefinitions();
  return definitions.find((d) => d.metricKey === metricKey);
}
